//@flow

import React from "react"
import { connect } from "react-redux"
import { compose } from "react-apollo"
import { push } from "react-router-redux"
import { FormattedMessage, injectIntl } from "react-intl"
import { TweenMax, Cubic, Back } from "gsap"
import { isModal } from "../../Modal"
import SVGLoader from "../../UI/SVGLoader"
import { withCurrentKioskData } from "../queries"
import { restartSession, sessionStartTime, sessionLengthMature } from "../redux"
import CTA from "../Shared/CTA"
import iconDaily from "../Icons/iconDaily.svg"
import iconTalk from "../Icons/iconTalk.svg"
import iconForecast from "../Icons/iconForecast.svg"
import iconLungs from "../Icons/iconLungs.svg"
import iconFire from "../Icons/iconFire.svg"
import iconSurvey from "../Icons/iconSurvey.svg"
import CloudsRasterized from "./CloudsRasterized"
import DefaultHero from "./DefaultHero"
import FormattedTitles from "./FormattedTitles"
import PrimaryOption, { PrimaryOptionList } from "./PrimaryOption"
import mountains from "./mountains.svg"
import sun from "./sun.svg"
import {
  Background,
  BeginButton,
  Regions,
  TitleRegion,
  ActionRegion,
  HomeContent,
  Mountains,
  Sun,
  SolidBackground,
  Title,
  EmphasizedTitle,
  Content,
  Description,
  HeadingGroup
} from "./UI"

import type { CurrentKiosk } from "../queries"
import type { RouterHistory, Match } from "react-router"
import type { IntlShape } from "react-intl"

type Props = {
  intl: IntlShape,
  isModal: boolean,
  history: RouterHistory,
  match: Match,
  data: CurrentKiosk,
  restartSession: Function,
  sessionStartTime: number,
  sessionLengthMature: boolean
}

type State = { currentInterval: number }

const intervalDuration = 10000

class AttractLoopView extends React.Component<Props, State> {
  options: Array<HTMLElement>
  slideText: Array<HTMLElement>
  container: ?HTMLElement
  homeScreen: ?HTMLElement
  attractLoop: ?HTMLElement
  beginButton: ?HTMLElement
  homeBackground: ?HTMLElement
  sun: ?HTMLElement
  mountains: ?HTMLElement
  blurElement: { a: number }
  slideInterval: ?number
  ctas: string[]

  constructor(props) {
    super(props)
    this.options = []
    this.slideText = []
    this.container = null
    this.beginButton = null
    this.blurElement = { a: 0 }
    this.state = { currentInterval: 0, homeVisible: false }
    this.ctas = ["initial", "dothis", "thatsme", "resist"]
  }

  componentDidMount = () => {
    const { match: { params } } = this.props
    if (params.scene === "default") {
      this.showHomeScreen()
      this.resetAttractScreen()
    } else {
      this.resetHomeScreen()
      this.startAttract()
    }
  }

  componentWillUnmount = () => {
    this.stopAttract()
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { isModal, match: { params } } = this.props
    const { currentInterval } = this.state
    if (prevProps.isModal !== isModal) {
      TweenMax.to(this.blurElement, 0.5, {
        a: isModal ? 5 : 0,
        ease: Cubic.easeInOut,
        onUpdate: () => {
          TweenMax.set(this.container, {
            webkitFilter: "blur(" + this.blurElement.a + "px)",
            filter: "blur(" + this.blurElement.a + "px)"
          })
        }
      })
    }
    if (prevState.currentInterval !== currentInterval) {
      this.pulseBeginButton()
    }
    if (prevProps.match.params.scene !== params.scene) {
      params.scene === "default" ? this.showHomeScreen() : this.hideHomeScreen()
    }
  }

  startAttract = () => {
    const maxInterval = this.ctas.length * 2 - 3
    this.slideInterval = setInterval(() => {
      const currentInterval =
        this.state.currentInterval === maxInterval
          ? 0
          : this.state.currentInterval + 1
      this.setState(() => ({ currentInterval }))
    }, intervalDuration)
    this.pulseBeginButton()
  }

  stopAttract = () => {
    if (this.slideInterval) {
      clearInterval(this.slideInterval)
    }
  }

  resetAttractScreen = () => {
    TweenMax.set(this.attractLoop, { y: -1080 })
  }

  resetHomeScreen = () => {
    TweenMax.set(this.options, { y: "+=350", opacity: 0 })
    TweenMax.set(this.slideText, { y: "-=40", opacity: 0 })
    TweenMax.set([this.mountains, this.sun, this.homeScreen], { y: 1080 })
    TweenMax.set(this.homeBackground, { opacity: 0 })
  }

  pulseBeginButton = () => {
    if (this.beginButton) {
      TweenMax.set(this.beginButton, { scale: 1.5 })
      TweenMax.to(this.beginButton, 1, { scale: 1, ease: Back.easeOut })
    }
  }

  showHomeScreen = () => {
    this.resetHomeScreen()
    TweenMax.staggerTo(
      [this.mountains, this.sun, this.homeScreen],
      1,
      { y: 0, ease: Back.easeInOut },
      0.25
    )
    TweenMax.to(this.attractLoop, 1, { y: -1080, ease: Back.easeInOut })
    TweenMax.to(this.homeBackground, 0.5, {
      opacity: 1,
      ease: Back.easeOut,
      delay: 0
    })

    setTimeout(() => this.showText(), 1000)
    setTimeout(() => this.showOptions(), 1500)
    this.stopAttract()
  }

  hideHomeScreen = () => {
    this.startAttract()
    TweenMax.staggerTo(
      [this.mountains, this.sun, this.homeScreen],
      1,
      { y: 1080, ease: Back.easeInOut },
      0.15
    )
    TweenMax.to(this.homeBackground, 0.5, {
      opacity: 0,
      ease: Back.easeOut
    })
    TweenMax.to(this.attractLoop, 1, { y: 0, ease: Back.easeInOut })
  }

  showText = () =>
    TweenMax.staggerTo(
      this.slideText.filter(el => el !== null),
      0.5,
      {
        y: "0",
        opacity: 1,
        ease: Cubic.easeOut,
        delay: 0.5
      },
      0.25
    )

  showOptions = () =>
    TweenMax.staggerTo(
      this.options.filter(el => el !== null),
      0.35,
      { y: "0", opacity: 1, ease: Back.easeOut },
      0.1
    )

  handleOptionSelect = (pathname: string, modal: ?boolean) => {
    const { history: { push } } = this.props
    push({ pathname, state: { modal } })
  }

  render() {
    const {
      intl: { formatMessage },
      data,
      history,
      match: { params },
      restartSession,
      sessionLengthMature
    } = this.props
    const { currentInterval } = this.state
    const showTitle = currentInterval % 2 > 0
    const shortName = data.currentKiosk ? data.currentKiosk.shortName : ""
    return (
      <Background innerRef={el => (this.container = el)}>
        <Regions zIndex={3} innerRef={el => (this.attractLoop = el)}>
          <TitleRegion>
            {showTitle ? (
              <FormattedTitles
                titleKey={this.ctas[Math.ceil(currentInterval / 2)]}
              />
            ) : (
              <DefaultHero
                locationName={
                  data.loading || !data.currentKiosk
                    ? "..."
                    : data.currentKiosk.friendlyName
                }
              />
            )}
          </TitleRegion>
          <ActionRegion>
            <BeginButton
              innerRef={el => (this.beginButton = el)}
              onClick={() => {
                restartSession()
                history.push("/kiosk/home/default")
              }}
            >
              <FormattedMessage
                id={`kiosk.home.begin.${this.ctas[
                  showTitle ? Math.ceil(currentInterval / 2) : 0
                ]}`}
              />
            </BeginButton>
          </ActionRegion>
        </Regions>
        <CloudsRasterized zIndex={2} hidden={params.scene === "default"} />
        <HomeContent zIndex={3} innerRef={el => (this.homeScreen = el)}>
          <Content>
            <HeadingGroup>
              <Title innerRef={el => this.slideText.push(el)}>
                <FormattedMessage id="kiosk.home.title" />
              </Title>
              <EmphasizedTitle innerRef={el => this.slideText.push(el)}>
                <FormattedMessage id="kiosk.home.emphasizedTitle" />
              </EmphasizedTitle>
              <Description innerRef={el => this.slideText.push(el)}>
                {formatMessage({ id: "kiosk.home.description" }).replace(
                  "[SHORT NAME]",
                  shortName
                )}
              </Description>
              <CTA
                innerRef={el => this.slideText.push(el)}
                direction="down"
                arrowFill="#002943"
                appearance="blue"
                centered={true}
                message={formatMessage({ id: "kiosk.home.callToAction" })}
              />
            </HeadingGroup>
          </Content>
          <PrimaryOptionList>
            <PrimaryOption
              height="132px"
              width="132px"
              path={iconForecast}
              innerRef={el => el && this.options.push(el)}
              title={[
                formatMessage({ id: "kiosk.home.options.current" }),
                shortName
              ]}
              onSelect={this.handleOptionSelect}
              destination="/kiosk/section/current"
            />
            <PrimaryOption
              height="156px"
              width="124px"
              path={iconFire}
              innerRef={el => el && this.options.push(el)}
              title={[
                formatMessage({ id: "kiosk.home.options.woodburning" }),
                formatMessage({ id: "kiosk.home.options.woodburning2" })
              ]}
              onSelect={this.handleOptionSelect}
              destination="/kiosk/section/woodburning"
            />
            <PrimaryOption
              height="120px"
              width="128px"
              path={iconLungs}
              innerRef={el => el && this.options.push(el)}
              title={[
                formatMessage({ id: "kiosk.home.options.body" }),
                formatMessage({ id: "kiosk.home.options.body2" })
              ]}
              onSelect={this.handleOptionSelect}
              destination="/kiosk/section/body"
            />
            <PrimaryOption
              height="117px"
              width="117px"
              path={iconDaily}
              innerRef={el => el && this.options.push(el)}
              title={[
                formatMessage({ id: "kiosk.home.options.commit" }),
                formatMessage({ id: "kiosk.home.options.commit2" })
              ]}
              onSelect={this.handleOptionSelect}
              destination="/kiosk/section/commit"
            />
            <PrimaryOption
              height="132px"
              width="141px"
              path={iconTalk}
              innerRef={el => el && this.options.push(el)}
              title={[
                formatMessage({ id: "kiosk.home.options.more" }),
                formatMessage({ id: "kiosk.home.options.more2" })
              ]}
              onSelect={this.handleOptionSelect}
              destination="/kiosk/section/more"
            />
            <PrimaryOption
              disabled={!sessionLengthMature}
              height="115px"
              width="81px"
              path={iconSurvey}
              innerRef={el => el && this.options.push(el)}
              title={[
                formatMessage({ id: "kiosk.home.options.survey" }),
                formatMessage({ id: "kiosk.home.options.survey2" })
              ]}
              onSelect={this.handleOptionSelect}
              destination="/kiosk/survey"
              modal={true}
            />
          </PrimaryOptionList>
        </HomeContent>
        <Mountains innerRef={el => (this.mountains = el)}>
          <SVGLoader path={mountains} height="100%;" width="100%" />
        </Mountains>
        <Sun innerRef={el => (this.sun = el)}>
          <SVGLoader path={sun} height="100%;" width="100%" />
        </Sun>
        <SolidBackground innerRef={el => (this.homeBackground = el)} />
      </Background>
    )
  }
}

const AttractLoopViewWithData = compose(withCurrentKioskData)(AttractLoopView)

export default injectIntl(
  connect(
    state => ({
      isModal: isModal(state),
      sessionStartTime: sessionStartTime(state),
      sessionLengthMature: sessionLengthMature(state)
    }),
    { push, restartSession }
  )(AttractLoopViewWithData)
)



// WEBPACK FOOTER //
// ./src/Kiosk/AttractLoopView/index.js